import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, Grid, Text } from '@tremor/react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { Sparkline, type SparklinePoint } from '@/components/Sparkline';
import { getSatisfacao, extractErrorMessage } from '@/lib/api';

interface TrendPoint {
  semana: string;
  up: number | null;
  down: number | null;
  satisfacao: number | null;
}

interface DownItem {
  created_at: string | null;
  processo_id: number | null;
  comentario: string | null;
}

interface SatisfacaoData {
  total_up: number | null;
  total_down: number | null;
  total_votos: number | null;
  taxa_satisfacao: number | null;
  taxa_resposta: number | null;
  downloads: number | null;
  tendencia: TrendPoint[];
  downs_recentes: DownItem[];
}

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 0): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function SatisfacaoCard() {
  const { data, isLoading, error } = useQuery<SatisfacaoData>({
    queryKey: ['satisfacao'],
    queryFn: async () => (await getSatisfacao()).data,
    refetchInterval: 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar satisfação')
    : null;

  const totalVotos = data?.total_votos ?? 0;
  const semVotos = !isLoading && !errMsg && totalVotos === 0;

  // Sparkline = satisfação semanal; cai pro volume de votos se faltar taxa.
  const sparkData: SparklinePoint[] = (data?.tendencia ?? []).map((t) => ({
    date: fmtDate(t.semana),
    value:
      t.satisfacao !== null && t.satisfacao !== undefined
        ? Math.round(t.satisfacao * 100)
        : (t.up ?? 0) + (t.down ?? 0),
  }));

  const downs = data?.downs_recentes ?? [];

  return (
    <section className="space-y-2">
      <Text className="text-xs uppercase tracking-wide text-slate-400">
        Satisfação com o cálculo
      </Text>

      {semVotos ? (
        <Card className="border border-slate-700/40">
          <div className="flex items-center gap-2 text-slate-300">
            <ThumbsUp className="h-4 w-4" />
            <Text className="text-slate-300">Sem votos ainda</Text>
          </div>
          <Text className="pt-1 text-sm text-slate-400">
            O feature de feedback (👍/👎) acabou de entrar no ar. Quando os
            usuários começarem a votar no cálculo, a satisfação, a tendência e os
            👎 acionáveis aparecem aqui.
          </Text>
          <Text className="pt-2 text-xs text-slate-500">
            {fmtInt(data?.downloads)} downloads no período · 0 votos
          </Text>
        </Card>
      ) : (
        <>
          <Grid numItems={1} numItemsSm={2} numItemsLg={3} className="gap-4">
            <KPICard
              label="Satisfação 👍"
              value={isLoading ? null : fmtPct(data?.taxa_satisfacao)}
              loading={isLoading}
              error={errMsg}
              hint={`${fmtInt(data?.total_up)} 👍 · ${fmtInt(
                data?.total_down,
              )} 👎 (${fmtInt(totalVotos)} votos)`}
            />
            <KPICard
              label="Taxa de resposta"
              value={isLoading ? null : fmtPct(data?.taxa_resposta)}
              loading={isLoading}
              error={errMsg}
              hint={`${fmtInt(totalVotos)} de ${fmtInt(
                data?.downloads,
              )} downloads votaram`}
            />
            <Card>
              <Text>Tendência semanal</Text>
              <Text className="text-xs text-slate-400">
                % satisfação (últimas ~8 semanas)
              </Text>
              <div className="pt-2">
                <Sparkline
                  data={sparkData}
                  loading={isLoading}
                  color="emerald"
                  empty="sem tendência ainda"
                />
              </div>
            </Card>
          </Grid>

          <Card className="border border-red-700/40 bg-red-900/10">
            <div className="mb-2 flex items-center gap-2">
              <ThumbsDown className="h-4 w-4 text-red-300" />
              <Text className="text-red-200">👎 recentes</Text>
            </div>
            {isLoading && <Text className="text-sm text-slate-400">carregando…</Text>}
            {!isLoading && errMsg && (
              <div className="text-sm text-red-400" role="alert">
                {errMsg}
              </div>
            )}
            {!isLoading && !errMsg && downs.length === 0 && (
              <Text className="text-sm text-slate-400">
                Nenhum 👎 no período — só elogios por enquanto.
              </Text>
            )}
            {!isLoading && !errMsg && downs.length > 0 && (
              <ol className="space-y-2">
                {downs.map((d, i) => (
                  <li
                    key={`${d.processo_id ?? 'x'}-${d.created_at ?? i}`}
                    className="flex gap-3 border-b border-slate-800/50 pb-2 last:border-0"
                  >
                    <time className="w-12 shrink-0 pt-0.5 text-xs text-slate-500">
                      {fmtDate(d.created_at)}
                    </time>
                    <div className="flex-1">
                      {d.processo_id !== null ? (
                        <Link
                          to={`/cases/${d.processo_id}` as never}
                          className="text-sm font-medium text-blue-300 hover:underline"
                        >
                          caso #{d.processo_id}
                        </Link>
                      ) : (
                        <span className="text-sm text-slate-400">
                          (sem caso)
                        </span>
                      )}
                      <p className="mt-0.5 text-xs text-slate-300">
                        {d.comentario ?? (
                          <span className="text-slate-500">sem comentário</span>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </>
      )}
    </section>
  );
}
